import PropTypes from 'prop-types';
import React, { useState, useLayoutEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard, fetchEmployersRequest } from '../../actions';
import AutocompletePreload from '../../components/AutocompletePreload';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { currentlyEmployedSelector, employersSelector } from '../../reducers/selectors';

const EmployerPage = ({
  initialEmployer,
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchFetchEmployersRequest,
  currentlyEmployed,
  employers,
}) => {
  const [employer, setEmployer] = useState(initialEmployer);
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (employers.length === 0) {
      dispatchFetchEmployersRequest();
    }
  }, [employers, dispatchFetchEmployersRequest]);

  const changeEmployer = data => setEmployer(data);

  const employerNotChosen = employer === null;

  const handleSubmit = event => {
    event.preventDefault();
    if (employerNotChosen || isInvalid) return;

    analytics.sendEvent('Click - Next', 'employer');

    const { label: employerName } = employer;
    const payload = payloadFactory.createAddPageDataPayload(
      'employer',
      employer,
      'employer',
      employerName,
    );
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const isInvalid = useMemo(() => {
    if (!employer) return;
    const { label: employerName } = employer;
    if (employerName.length > 200) {
      setError('Employer name cannot exceed 200 characters');
      return true;
    } else {
      return false;
    }
  }, [employer]);

  const pageTitle = currentlyEmployed
    ? 'What company do you work for?'
    : 'Where did you last work?';

  const pageHelp = currentlyEmployed
    ? 'Telling us about your company gives us more information on your future employment.'
    : 'Your company will help us better understand your work experience';

  return (
    <PageLayout title={pageTitle} helpText={pageHelp}>
      <form onSubmit={handleSubmit}>
        <div className="row row--align-middle">
          <div className="col col--12/16-small">
            <AutocompletePreload
              defaultOption={employer}
              onChange={changeEmployer}
              options={employers}
              placeholder="Search for your company"
              noOptionsMessage="Enter your company name to continue."
              loadingMessage="Loading data. Please wait..."
              type="creatable"
              disableFuzzyMatch={true}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col--auto">
            <Button type="submit" isDisabled={employerNotChosen || isInvalid}>
              Next
            </Button>
          </div>
        </div>
        {isInvalid && error !== null && <div className="row-spacer text-error">{error}</div>}
      </form>
    </PageLayout>
  );
};

EmployerPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchFetchEmployersRequest: PropTypes.func.isRequired,
  currentlyEmployed: PropTypes.bool.isRequired,
  initialEmployer: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
  }),
  employers: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

EmployerPage.defaultProps = {
  initialEmployer: null,
  employers: [],
};

export const mapStateToProps = reduxStore => ({
  initialEmployer: reduxStore.builder.ui.employer,
  currentlyEmployed: currentlyEmployedSelector(reduxStore),
  employers: employersSelector(reduxStore),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchFetchEmployersRequest: fetchEmployersRequest,
};

export { EmployerPage };

export default connect(mapStateToProps, mapDispatchToProps)(EmployerPage);
